<template>
  <Loader
    :loader="loading?.activities"
  />
  <main v-if="!loading?.activities || !loading?.classes || !loading?.courses">
    <Button class="my-4 btn-simple flex gap-4" @click="$router.go(-1)">
      <i class="pi pi-chevron-left"></i>
      <span class="text-h-primary-500">
        {{ $t("labels.generic", { en: "Activities", pt: "Atividades" }) }}
      </span>
    </Button>
    <form @submit.prevent="onSubmit" class="py-4">
      <div class="flex flex-col px-8">
        <div class="flex flex-col gap-4">
          <h4 class="w-full">
            {{ activityId ? "Editar atividade" : "Criar atividade" }}
          </h4>
          <div class="flex flex-wrap gap-6 py-4">
            <div>
              <Dropdown
                id="list-courses"
                class="w-[320px]"
                :placeholder="
                  $t('labels.generic', {
                    pt: 'Selecionar Curso',
                    en: 'Select Course',
                  })
                "
                ref="dropdownCourses"
                :emptyMessage="
                  $t('labels.generic', {
                    en: 'No courses relationed',
                    pt: 'Nenhum curso relacionado',
                  })
                "
                :options="listCourses"
                optionLabel="name"
                optionValue="id"
                v-model="form.courseId"
                @change="getAllClasses"
                :loading="loading.courses"
                :showClear="true"
                :disabled="loading.courses"
              />
            </div>

            <div>
              <Dropdown
                id="list-classes"
                class="w-[320px]"
                :class="[{ 'p-invalid': v$.classroomId.$error }]"
                :placeholder="
                  $t('labels.generic', {
                    pt: 'Selecionar Turmas*',
                    en: 'Select CLasses*',
                  })
                "
                :emptyMessage="
                  $t('labels.generic', {
                    en: 'No classes relationed',
                    pt: 'Nenhuma turma relacionada',
                  })
                "
                :options="listClasses"
                optionLabel="name"
                optionValue="id"
                v-model="form.classroomId"
                ref="dropdownClasses"
                :loading="loading.classes"
                :disabled="!form.courseId"
              />
              <Errors :errors="v$.classroomId.$errors" />
            </div>
          </div>
        </div>
        <div class="flex flex-col md:flex-row w-full gap-6">
          <div class="flex flex-col md:w-1/2 w-full" id="activity-field">
            <div class="flex flex-col gap-2 py-4">
              <label for="acitivity-title">{{ "Nome da Atividade*" }}</label>
              <InputText
                :placeholder="
                  $t('labels.generic', {
                    en: 'Type activity name',
                    pt: 'Digite o nome da atividade',
                  })
                "
                v-model="form.title"
                :class="[{ 'p-invalid': v$.title.$error }]"
                id="activity-title"
              />
              <Errors :errors="v$.title.$errors" />
            </div>
            <div class="flex flex-col gap-2 py-4">
              <label for="description-input">
                {{ "Descrição da Atividade*" }}</label
              >
              <div>
                <Editor
                  id="description-input"
                  v-model="form.description"
                  :class="[{ 'p-invalid': v$.description.$error }]"
                  editorStyle="height: 230px"
                >
                  <template v-slot:toolbar>
                    <span class="ql-formats">
                      <button
                        v-tooltip.bottom="'Bold'"
                        class="ql-bold"
                      ></button>
                      <button
                        v-tooltip.bottom="'Italic'"
                        class="ql-italic"
                      ></button>
                      <button
                        v-tooltip.bottom="'Underline'"
                        class="ql-underline"
                      ></button>
                      <button
                        v-tooltip.bottom="'Bullet List'"
                        class="ql-list"
                        value="bullet"
                      ></button>
                      <button
                        v-tooltip.bottom="'Link'"
                        class="ql-link"
                        value="link"
                      ></button>
                    </span>
                  </template>
                </Editor>
              </div>
              <Errors
                :errors="v$.description.$errors"
                :maxLength="999"
                :actualLength="form?.description?.length"
              />
            </div>
          </div>
          <div class="flex flex-col md:w-1/2 w-full" id="activity-term">
            <span class="flex flex-wrap justify-between gap-2">
              <div
                class="flex flex-col col-span-2 row-start-3 gap-2 py-4 w-full lg:w-56"
              >
                <label>{{ "Data de Início" }}</label>
                <Calendar
                  placeholder="dd/mm/aaaa"
                  :class="[{ 'p-invalid': v$.startDate.$error }]"
                  v-model="form.startDate as Date"
                  dateFormat="dd/mm/yy"
                  :minDate="today"
                />
                <Errors :errors="v$.startDate.$errors" />
              </div>
              <div
                class="flex flex-col col-span-2 row-start-3 gap-2 py-4 w-full lg:w-56"
              >
                <label>{{ "Data de Entrega*" }}</label>
                <Calendar
                  placeholder="dd/mm/aaaa"
                  :class="[{ 'p-invalid': v$.finalDate.$error }]"
                  v-model="form.finalDate as Date"
                  dateFormat="dd/mm/yy"
                  :minDate="today"
                />
                <Errors :errors="v$.finalDate.$errors" />
              </div>
              <div
                class="flex flex-col col-span-2 row-start-3 gap-2 py-4 w-full lg:w-56"
              >
                <label>{{ "Hora de Entrega*" }}</label>
                <InputMask
                  id="final-hour"
                  placeholder="00:00"
                  v-model="form.finalHour"
                  mask="99:99"
                  :class="[{ 'p-invalid': v$.finalHour.$error }]"
                />
                <Errors :errors="v$.finalHour.$errors" />
              </div>
            </span>
            <div class="py-4">
              <label>{{ "Anexar arquivo" }}</label>
              <Dropzone
                @file-selected="setFileActivities"
                v-model:materials="materials"
                @dismissedMaterial="onDismissMaterial"
                key="dropzone"
              />
            </div>
            <div class="flex flex-wrap w-full gap-2 justify-between">
              <div class="flex flex-col gap-2 py-4 flex-1">
                <label>{{ "Nota Máxima*" }}</label>
                <InputNumber
                  placeholder="Atribua um valor máximo"
                  :min="0"
                  v-model="form.scoreRange"
                  :class="[{ 'p-invalid': v$.scoreRange.$error }]"
                  mode="decimal"
                />
                <Errors :errors="v$.scoreRange.$errors" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="flex px-8 gap-2">
        <Button
          class="btn-outline"
          :label="$t('labels.generic', { en: 'Cancel', pt: 'Cancelar' })"
          @click="goBack"
        />
        <Button
          class="btn-primary"
          :label="$t('labels.generic', { en: 'Save', pt: 'Salvar' })"
          type="submit"
          :loading="loading.activities"
        />
      </div>
    </form>
  </main>
</template>
<script lang="ts">
import { FormActivity } from "@/models/activity.model";
import useVuelidate from "@vuelidate/core";
import { validations } from "./form.validations";
import { defineAsyncComponent, defineComponent, reactive, ref } from "vue";
import { CoursesRest } from "@/services/rest/courses.rest";
import { ClassesRest } from "@/services/rest/classes.rest";
import { ActivityRest } from "@/services/rest/activity.rest";
import { buildForm, updateFinalDate, updateStartDate } from "./activity.utils";
import { ToastFactory } from "@/utils/toast-factory.util";
import { Messages } from "@/utils/messages.util";
import { ActivityMapper } from "./activity.mapper";
import { GoToRoute } from "@/utils/go-to.util";
const Dropzone = defineAsyncComponent(() => import("./dropzone.vue"));
export default defineComponent({
  data() {
    return {
      activityId: this.$route.params.id,
      coursesParams: { page: 0, size: 100, status: "PUBLISHED" },
      classesParams: { page: 0, size: 100, courseId: "" },
      loading: {
        activities: false,
      },
      editorFormats: ["bold", "italic", "underline", "bulletList"],
    };
  },
  computed: {
    restCourse(): CoursesRest {
      return new CoursesRest();
    },
    restClasses(): ClassesRest {
      return new ClassesRest();
    },
    restActivities(): ActivityRest {
      return new ActivityRest();
    },
    mapper(): ActivityMapper {
      return new ActivityMapper();
    },
    go(): GoToRoute {
      return new GoToRoute();
    },
  },

  components: { Dropzone },
  setup() {
    const form = reactive(new FormActivity());
    const rules = validations(form);
    const v$ = useVuelidate(rules, form);
    const materials = ref([] as any[]);
    const today = new Date();
    const listCourses = ref([]);
    const listClasses = ref([]);
    const loading = ref({ courses: false, classes: false, activities: false });
    function setFileActivities(files) {
      form.files = files;
    }

    return {
      form,
      listCourses,
      listClasses,
      today,
      v$,
      setFileActivities,
      loading,
      materials,
    };
  },
  methods: {
    onSubmit() {
      this.v$.$validate();
      if (this.v$.$invalid) return;
      updateFinalDate(this.form);
      updateStartDate(this.form);
      if (this.activityId) this.update(this.activityId as string);
      else this.create();
    },
    getAllCourses() {
      this.loading.courses = true;
      this.restCourse.getCourses(this.coursesParams).subscribe({
        next: (response) => {
          this.listCourses = response.content;
        },
        complete: () => {
          this.loading.courses = false;
          window.scrollTo(0, 0);
        },
        error: () => {
          this.listCourses = [];
        },
      });
    },
    getAllClasses() {
      if (this.form.courseId) this.classesParams.courseId = this.form.courseId;
      this.loading.classes = true;
      this.restClasses.getAllClasses(this.classesParams).subscribe({
        next: (response) => {
          this.listClasses = response.content;
        },
        complete: () => {
          this.loading.classes = false;
          window.scrollTo(0, 0);
        },
        error: () => {
          this.listClasses = [];
        },
      });
    },
    create() {
      const data = buildForm(this.form);
      this.loading.activities = true;
      this.restActivities.create(data).subscribe({
        next: () => {
          this.$toast.add(ToastFactory.success(Messages.ACTIVITY_CREATED));
          this.$router.push({ path: "/classes", name: "classes" });
        },
        error: () => {
          this.$toast.add(ToastFactory.error(Messages.UNDEFINED_ERROR));
          this.loading.activities = false;
        },
        complete: () => {
          this.loading.activities = false;
        },
      });
    },
    update(activityId: string) {
      const data = buildForm(this.form);
      this.loading.activities = true;
      this.restActivities.update(activityId, data).subscribe({
        next: () => {
          this.$toast.add(ToastFactory.success(Messages.ACTIVITY_UPDATED));
          this.$router.push({ path: "/classes", name: "classes" });
        },
        error: () => {
          this.$toast.add(ToastFactory.error(Messages.UNDEFINED_ERROR));
          this.loading.activities = false;
        },
        complete: () => {
          this.loading.activities = false;
        },
      });
    },
    getActivityById() {
      this.loading.activities = true;
      this.restActivities.getById(this.activityId as string).subscribe({
        next: (res) => {
          Object.assign(this.form, this.mapper.toDomain(res));
          this.materials = this.form.materials;
          this.loading.activities = false;
        },
        error: () => {
          this.$router.go(-1);
        },
        complete: () => {
          this.loading.activities = false;
        },
      });
    },
    async onDismissMaterial(id: string) {
      if (!id || !this.form.materials) return;
      this.form.materials = this.form.materials?.filter(
        (material) => material.id !== id
      );
      this.form.materialsToRemove?.push(id);
    },
    goBack() {
      this.$router.go(-1);
    },
  },
  mounted() {
    this.getAllCourses();
    this.getAllClasses();
    if (this.activityId) this.getActivityById();
  },
  watch:{
    "form.courseId": function (newValue) {
      if (newValue) {
        this.getAllClasses();
      } else {
        this.listClasses = [];
        this.form.classroomId = undefined as any;
      }
    },
  }
});
</script>
